import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Grid,
} from '@mui/material';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocations, type Location } from '../../hooks/useLocations';
import { useCreateVehicle, useUpdateVehicle } from '../../hooks/useVehicles'; // ✅ ADDED: Import useUpdateVehicle
import type { VehicleCreate, VehicleUpdate } from '../../types/vrp'; // ✅ ADDED: Import VehicleUpdate type
import { 
  CapacityEditor, 
  TimeWindowsEditor, 
  SkillsSelector, 
  FormSection,
  type TimeWindow,
  type Skill 
} from '../common';

// Enhanced vehicle form schema with all database fields
const vehicleBreakSchema = z.object({
  id: z.number().optional(),
  service: z.number().min(0).default(1800), // 30 minutes default
  description: z.string().optional(),
  time_windows: z.array(z.object({
    start: z.number().min(0).max(86400),
    end: z.number().min(0).max(86400)
  })).default([]),
});

const vehicleStepSchema = z.object({
  type: z.enum(['start', 'job', 'pickup', 'delivery', 'break', 'end']),
  id: z.number().optional(),
  service_at: z.number().optional(),
  service_after: z.number().optional(),
});

// South African vehicle types for business categorization
const SA_VEHICLE_TYPES = [
  'bakkie-pickup',
  'small-truck', 
  'medium-truck',
  'rigid-truck',
  'tri-axle',
  'super-link',
  'tanker',
  'rigid-tanker',
  'flat-deck',
  'pantech',
  'refrigerated',
  'bulk-trailer',
  'dump-truck',
  'ldv',
  'curtain-side',
  'box-truck'
] as const;

// Transport modes for VROOM solver
const TRANSPORT_MODES = ['driving', 'cycling', 'walking'] as const;

const vehicleSchema = z.object({
  description: z.string().min(1, 'Vehicle description is required').max(255, 'Description must be less than 255 characters'),
  vehicle_type: z.enum(SA_VEHICLE_TYPES, { 
    errorMap: () => ({ message: 'Vehicle type selection is required' })
  }),
  transport_mode: z.enum(TRANSPORT_MODES, { 
    errorMap: () => ({ message: 'Transport mode selection is required' })
  }).default('driving'),
  start_location_id: z.number().min(1, 'Start location selection is required'),
  end_location_id: z.number().optional(),
  capacity: z.array(z.number().min(0, 'Capacity must be non-negative')).min(1, 'At least one capacity dimension is required').default([1000]),
  skills: z.array(z.number()).optional().default([]),
  // Enhanced time windows (replaces simple tw_start/tw_end)
  time_windows: z.array(z.object({
    start: z.number().min(0).max(86400),
    end: z.number().min(0).max(86400)
  })).default([{ start: 28800, end: 64800 }]), // Default 8 AM - 6 PM
  speed_factor: z.number().min(0.1).max(5.0).default(1.0),
  max_tasks: z.number().min(1).optional(),
  max_travel_time: z.number().min(1).optional(),
  max_distance: z.number().min(1).optional(),
  // Break scheduling
  breaks: z.array(vehicleBreakSchema).default([]),
  // Step configuration for complex routing
  steps: z.array(vehicleStepSchema).default([]),
  // Cost configuration
  cost_fixed: z.number().min(0).default(0),
  cost_per_hour: z.number().min(0).default(50),
  cost_per_km: z.number().min(0).default(1),
});

type VehicleFormData = z.infer<typeof vehicleSchema>;

interface VehicleFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: VehicleFormData) => void;
  projectId: number;
  vehicle?: any; // For edit mode - existing vehicle data
  initialData?: Partial<VehicleFormData>;
  onSuccess?: () => void; // Callback for successful submission
}

export const VehicleForm: React.FC<VehicleFormProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  projectId, 
  vehicle, // Existing vehicle for edit mode
  initialData, 
  onSuccess 
}) => {
  // Unified VRP architecture - get scenario and dataset context
  const { currentScenario, currentDataset } = null /* TODO: Replace with Toolpad navigation */;
  
  // Use dataset-aware location fetching for context-specific locations
  // FIXED: Simplify options structure to match useLocations hook expectations
  const locationOptions = currentDataset?.id ? {
    dataset_id: currentDataset.id,
    enabled: true
  } : { enabled: true };
  
  const { data: locations, isLoading: locationsLoading } = useLocations(
    projectId, 
    currentScenario?.id, 
    locationOptions
  );
  
  // Debug logging for location fetching
  console.log('🔍 [VehicleForm] Location fetching debug:', {
    projectId,
    scenarioId: currentScenario?.id,
    datasetId: currentDataset?.id,
    locationOptions,
    locationsCount: Array.isArray(locations) ? locations.length : 0,
    locationsLoading
  });
  const createVehicle = useCreateVehicle();
  const updateVehicle = useUpdateVehicle();
  
  // Context validation
  const hasValidContext = projectId && currentScenario && currentDataset;
  
  // Determine if we're in edit mode
  const isEditMode = !!vehicle;
  
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      description: vehicle?.description || initialData?.description || '',
      vehicle_type: vehicle?.vehicle_type || initialData?.vehicle_type || 'bakkie-pickup',
      transport_mode: vehicle?.transport_mode || vehicle?.profile || initialData?.transport_mode || 'driving', // Legacy support for 'profile'
      start_location_id: vehicle?.start_location_id || initialData?.start_location_id || 0,
      end_location_id: vehicle?.end_location_id || initialData?.end_location_id || undefined,
      capacity: vehicle?.capacity || initialData?.capacity || [1000],
      skills: vehicle?.skills || initialData?.skills || [],
      time_windows: vehicle?.time_windows || initialData?.time_windows || [{ start: 28800, end: 64800 }], // 8 AM - 6 PM
      speed_factor: vehicle?.speed_factor || initialData?.speed_factor || 1.0,
      max_tasks: vehicle?.max_tasks || initialData?.max_tasks || undefined,
      max_travel_time: vehicle?.max_travel_time || initialData?.max_travel_time || undefined,
      max_distance: vehicle?.max_distance || initialData?.max_distance || undefined,
      breaks: vehicle?.breaks || initialData?.breaks || [],
      steps: vehicle?.steps || initialData?.steps || [],
      cost_fixed: vehicle?.cost_fixed || initialData?.cost_fixed || 0,
      cost_per_hour: vehicle?.cost_per_hour || initialData?.cost_per_hour || 50,
      cost_per_km: vehicle?.cost_per_km || initialData?.cost_per_km || 1,
    },
  });

  const selectedStartLocationId = watch('start_location_id');
  const selectedEndLocationId = watch('end_location_id');
  
  // Type the locations properly to avoid 'unknown' type issues and ensure it's always an array
  const typedLocations = Array.isArray(locations) ? locations as Location[] : [];
  const selectedStartLocation = typedLocations.find(loc => loc.id === selectedStartLocationId);
  const selectedEndLocation = typedLocations.find(loc => loc.id === selectedEndLocationId);

  // Reset form when vehicle prop changes (for edit mode)
  useEffect(() => {
    if (vehicle) {
      console.log('🔄 [VehicleForm] Resetting form with vehicle data:', vehicle);
      reset({
        description: vehicle.description || '',
        vehicle_type: vehicle.vehicle_type || 'bakkie-pickup',
        transport_mode: vehicle.transport_mode || vehicle.profile || 'driving', // Legacy support
        start_location_id: vehicle.start_location_id || 0,
        end_location_id: vehicle.end_location_id || undefined,
        capacity: vehicle.capacity || [1000],
        skills: vehicle.skills || [],
        time_windows: vehicle.time_windows || [{ start: 28800, end: 64800 }],
        speed_factor: vehicle.speed_factor || 1.0,
        max_tasks: vehicle.max_tasks || undefined,
        max_travel_time: vehicle.max_travel_time || undefined,
        max_distance: vehicle.max_distance || undefined,
        breaks: vehicle.breaks || [],
        steps: vehicle.steps || [],
        cost_fixed: vehicle.cost_fixed || 0,
        cost_per_hour: vehicle.cost_per_hour || 50,
        cost_per_km: vehicle.cost_per_km || 1,
      });
    } else {
      // Reset to empty form for create mode
      console.log('🔄 [VehicleForm] Resetting form for create mode');
      reset({
        description: initialData?.description || '',
        vehicle_type: initialData?.vehicle_type || 'bakkie-pickup',
        transport_mode: initialData?.transport_mode || 'driving',
        start_location_id: initialData?.start_location_id || 0,
        end_location_id: initialData?.end_location_id || undefined,
        capacity: initialData?.capacity || [1000],
        skills: initialData?.skills || [],
        time_windows: initialData?.time_windows || [{ start: 28800, end: 64800 }],
        speed_factor: initialData?.speed_factor || 1.0,
        max_tasks: initialData?.max_tasks || undefined,
        max_travel_time: initialData?.max_travel_time || undefined,
        max_distance: initialData?.max_distance || undefined,
        breaks: initialData?.breaks || [],
        steps: initialData?.steps || [],
        cost_fixed: initialData?.cost_fixed || 0,
        cost_per_hour: initialData?.cost_per_hour || 50,
        cost_per_km: initialData?.cost_per_km || 1,
      });
    }
  }, [vehicle, reset, initialData]);

  // Auto-populate coordinates when locations are selected
  useEffect(() => {
    if (selectedStartLocation) {
      console.log(`Vehicle start location: ${selectedStartLocation.name} (${selectedStartLocation.location_lat}, ${selectedStartLocation.location_lon})`);
    }
    if (selectedEndLocation) {
      console.log(`Vehicle end location: ${selectedEndLocation.name} (${selectedEndLocation.location_lat}, ${selectedEndLocation.location_lon})`);
    }
  }, [selectedStartLocation, selectedEndLocation]);

  const onSubmitHandler: SubmitHandler<VehicleFormData> = async (data: VehicleFormData) => {
    try {
      // If external onSubmit is provided, use it (for testing)
      if (onSubmit) {
        onSubmit(data);
        reset();
        onClose();
        return;
      }

      if (isEditMode) {
        // ✅ FIXED: Implement actual vehicle update functionality
        console.log('🔄 Updating vehicle with data:', data);
        
        if (!selectedStartLocation) {
          throw new Error('Start location selection is required');
        }

        // Prepare update data that matches the API interface
        const updateData: VehicleUpdate = {
          description: data.description,
          profile: data.transport_mode, // Map transport_mode to legacy profile field for API
          vehicle_type: data.vehicle_type, // New field for business categorization
          start_location_id: data.start_location_id,
          end_location_id: data.end_location_id || undefined,
          // Coordinates derived from selected locations
          start_lon: selectedStartLocation.location_lon || 0,
          start_lat: selectedStartLocation.location_lat || 0,
          end_lon: selectedEndLocation?.location_lon || selectedStartLocation.location_lon || 0,
          end_lat: selectedEndLocation?.location_lat || selectedStartLocation.location_lat || 0,
          capacity: data.capacity,
          skills: data.skills,
          // Cost structure for API
          costs: {
            fixed: data.cost_fixed,
            per_hour: data.cost_per_hour,
            per_km: data.cost_per_km,
          },
          speed_factor: data.speed_factor,
          max_tasks: data.max_tasks,
          max_travel_time: data.max_travel_time,
          max_distance: data.max_distance,
        };
        
        // Call the actual update API
        await updateVehicle.mutateAsync({
          vehicleId: vehicle.id,
          vehicleData: updateData,
          projectId: projectId
        });
        
        console.log('✅ Vehicle updated successfully');
        reset();
        onClose();
        onSuccess?.();
      } else {
        // Internal submission logic for production use
        if (!selectedStartLocation) {
          throw new Error('Start location selection is required');
        }

        // Validate required context before creating vehicle data
        if (!currentScenario || !currentDataset) {
          throw new Error('Scenario and dataset context are required for vehicle creation');
        }

        const vehicleData: VehicleCreate = {
          description: data.description || '', // Ensure required field is provided
          profile: data.transport_mode, // Map transport_mode to legacy profile field for VROOM
          vehicle_type: data.vehicle_type, // New field for business categorization
          project_id: projectId,
          scenario_id: typeof currentScenario?.id === 'number' ? currentScenario.id : (() => {
            console.error('❌ [VehicleForm] Invalid scenario context:', currentScenario);
            throw new Error('Invalid scenario context: scenario ID is not a number');
          })(), // REQUIRED: Scenario context
          dataset_id: typeof currentDataset?.id === 'number' ? currentDataset.id : (() => {
            console.error('❌ [VehicleForm] Invalid dataset context:', currentDataset);
            throw new Error('Invalid dataset context: dataset ID is not a number');
          })(),   // REQUIRED: Dataset context
          start_location_id: data.start_location_id,
          end_location_id: data.end_location_id || undefined,
          // Coordinates are derived from selected locations - no manual entry
          start_lon: selectedStartLocation.location_lon || 0,
          start_lat: selectedStartLocation.location_lat || 0,
          end_lon: selectedEndLocation?.location_lon || selectedStartLocation.location_lon || 0,
          end_lat: selectedEndLocation?.location_lat || selectedStartLocation.location_lat || 0,
          // Convert time_windows to single time_window for API compatibility
          time_window: data.time_windows.length > 0 ? {
            start: data.time_windows[0].start,
            end: data.time_windows[0].end
          } : undefined,
          // Convert to proper cost structure
          costs: {
            fixed: data.cost_fixed,
            per_hour: data.cost_per_hour,
            per_km: data.cost_per_km,
          },
        };
        
        await createVehicle.mutateAsync(vehicleData);
        reset();
        onClose();
        onSuccess?.();
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} vehicle:`, error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Sample skills data - in a real app, this would come from an API
  const availableSkills: Skill[] = [
    { id: 1, name: 'Heavy Lifting', description: 'Can handle heavy cargo', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 2, name: 'Refrigerated Transport', description: 'Temperature-controlled delivery', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 3, name: 'Hazmat Certified', description: 'Hazardous materials handling', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 4, name: 'Oversized Load', description: 'Can transport large items', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 5, name: 'Express Delivery', description: 'Priority and time-sensitive deliveries', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  ];

  // ✅ ADDED: Show update-specific errors
  const currentError = isEditMode ? updateVehicle.error : createVehicle.error;
  const isCurrentlySubmitting = isEditMode ? (isSubmitting || updateVehicle.isPending) : (isSubmitting || createVehicle.isPending);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEditMode ? `Edit Vehicle: ${vehicle?.description}` : 'Add New Vehicle'}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {/* ✅ FIXED: Show appropriate error for create or update operations */}
          {currentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {currentError instanceof Error ? currentError.message : `Failed to ${isEditMode ? 'update' : 'create'} vehicle`}
            </Alert>
          )}
          
          {/* Basic Information Section */}
          <FormSection title="Basic Information" subtitle="Vehicle identification and profile" required>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Vehicle Description"
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="e.g., Delivery Truck #1, Service Van B-12"
                    />
                  )}
                />
              </Grid>
              <Grid component="div" size={{ xs: 12, md: 4 }}>
                <Controller
                  name="vehicle_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.vehicle_type}>
                      <InputLabel>Vehicle Type *</InputLabel>
                      <Select {...field} label="Vehicle Type *">
                        <MenuItem value="bakkie-pickup">🚐 Bakkie/Pickup</MenuItem>
                        <MenuItem value="small-truck">🚚 Small Truck</MenuItem>
                        <MenuItem value="medium-truck">🚛 Medium Truck</MenuItem>
                        <MenuItem value="rigid-truck">🚛 Rigid Truck</MenuItem>
                        <MenuItem value="tri-axle">🚚 Tri-Axle</MenuItem>
                        <MenuItem value="super-link">🚛 Super-Link</MenuItem>
                        <MenuItem value="tanker">🚚 Tanker</MenuItem>
                        <MenuItem value="rigid-tanker">🚛 Rigid Tanker</MenuItem>
                        <MenuItem value="flat-deck">🚚 Flat Deck</MenuItem>
                        <MenuItem value="pantech">🚐 Pantech</MenuItem>
                        <MenuItem value="refrigerated">🧊 Refrigerated</MenuItem>
                        <MenuItem value="bulk-trailer">🚛 Bulk Trailer</MenuItem>
                        <MenuItem value="dump-truck">🚚 Dump Truck</MenuItem>
                        <MenuItem value="ldv">🚐 Light Delivery Vehicle</MenuItem>
                        <MenuItem value="curtain-side">🚚 Curtain-side</MenuItem>
                        <MenuItem value="box-truck">📦 Box Truck</MenuItem>
                      </Select>
                      {errors.vehicle_type && (
                        <FormHelperText>{errors.vehicle_type.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Transport Mode Section for VROOM Solver */}
          <FormSection title="Transport Mode" subtitle="Routing algorithm configuration for VROOM solver" required>
            <Grid container spacing={3}>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Controller
                  name="transport_mode"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.transport_mode}>
                      <InputLabel>Transport Mode *</InputLabel>
                      <Select {...field} label="Transport Mode *">
                        <MenuItem value="driving">🚗 Driving (Default)</MenuItem>
                        <MenuItem value="cycling">🚴 Cycling</MenuItem>
                        <MenuItem value="walking">🚶 Walking</MenuItem>
                      </Select>
                      {errors.transport_mode && (
                        <FormHelperText>{errors.transport_mode.message}</FormHelperText>
                      )}
                      <FormHelperText>
                        Transport mode affects route calculation speed and road type preferences in VROOM solver
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>VROOM Integration:</strong> Transport mode determines which road networks and speed profiles the optimization algorithm uses. 
                    Vehicle Type is for business categorization only.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </FormSection>

          {/* Location Configuration Section */}
          <FormSection title="Location Configuration" subtitle="Start and end locations for vehicle routes" required>
            <Grid container spacing={3}>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Controller
                  name="start_location_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.start_location_id}>
                      <InputLabel>Start Location *</InputLabel>
                      <Select
                        {...field}
                        label="Start Location *"
                        disabled={locationsLoading}
                      >
                        <MenuItem value={0} disabled>
                          <Typography color="text.secondary">
                            {locationsLoading ? 'Loading locations...' : 'Select start location'}
                          </Typography>
                        </MenuItem>
                        {typedLocations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            <Box>
                              <Typography variant="body1">{location.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {location.address || `${location.location_lat?.toFixed(4)}, ${location.location_lon?.toFixed(4)}`}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.start_location_id && (
                        <FormHelperText>{errors.start_location_id.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Controller
                  name="end_location_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>End Location (Optional)</InputLabel>
                      <Select
                        {...field}
                        value={field.value || ''}
                        label="End Location (Optional)"
                        disabled={locationsLoading}
                      >
                        <MenuItem value="">Same as start location</MenuItem>
                        {typedLocations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            <Box>
                              <Typography variant="body1">{location.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {location.address || `${location.location_lat?.toFixed(4)}, ${location.location_lon?.toFixed(4)}`}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Location Master Integration Alert */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Location Master Integration:</strong> This vehicle will be linked to selected locations. 
                Coordinates are automatically populated from the location master.
              </Typography>
            </Alert>
          </FormSection>

          {/* Capacity & Performance Section */}
          <FormSection title="Capacity & Performance" subtitle="Vehicle capacity and performance parameters">
            <Grid container spacing={3}>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Controller
                  name="capacity"
                  control={control}
                  render={({ field }) => (
                    <CapacityEditor
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.capacity?.message}
                      label="Vehicle Capacity"
                      helperText="Define weight, volume, or item count limits"
                    />
                  )}
                />
              </Grid>              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Controller
                    name="speed_factor"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <TextField
                        {...field}
                        value={value}
                        onChange={(e) => onChange(parseFloat(e.target.value) || 1.0)}
                        label="Speed Factor"
                        type="number"
                        fullWidth
                        error={!!errors.speed_factor}
                        helperText={errors.speed_factor?.message || 'Multiplier for travel speeds (0.1 - 5.0)'}
                        inputProps={{ min: 0.1, max: 5.0, step: 0.1 }}
                      />
                    )}
                  />
                  <Controller
                    name="max_tasks"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <TextField
                        {...field}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        label="Maximum Tasks (Optional)"
                        type="number"
                        fullWidth
                        error={!!errors.max_tasks}
                        helperText={errors.max_tasks?.message || 'Maximum number of tasks per route'}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </FormSection>

          {/* Operating Hours Section */}
          <FormSection title="Operating Hours" subtitle="Define when this vehicle is available">
            <Controller
              name="time_windows"
              control={control}
              render={({ field }) => (
                <TimeWindowsEditor
                  value={field.value as TimeWindow[]}
                  onChange={field.onChange}
                  error={errors.time_windows?.message}
                  label="Available Time Windows"
                  helperText="Add time periods when vehicle can operate"
                />
              )}
            />
          </FormSection>

          {/* Vehicle Skills Section */}
          <FormSection title="Vehicle Skills" subtitle="Capabilities and certifications">
            <Controller
              name="skills"
              control={control}
              render={({ field }) => (
                <SkillsSelector
                  selectedSkills={field.value || []}
                  onSkillsChange={field.onChange}
                  skills={availableSkills}
                  error={!!errors.skills}
                  label="Vehicle Skills"
                  helperText="Select skills this vehicle possesses"
                />
              )}
            />
          </FormSection>
          {/* Advanced Constraints Section */}
          <FormSection title="Advanced Constraints" subtitle="Travel and distance limitations" defaultExpanded={false}>
            <Grid container spacing={3}>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Controller
                  name="max_travel_time"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      label="Maximum Travel Time (Optional)"
                      type="number"
                      fullWidth
                      error={!!errors.max_travel_time}
                      helperText={errors.max_travel_time?.message || 'Maximum travel time in seconds'}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  )}
                />
              </Grid>
              <Grid component="div" size={{ xs: 12, md: 6 }}>
                <Controller
                  name="max_distance"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      label="Maximum Distance (Optional)"
                      type="number"
                      fullWidth
                      error={!!errors.max_distance}
                      helperText={errors.max_distance?.message || 'Maximum travel distance in meters'}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Cost Configuration Section */}
          <FormSection title="Cost Configuration" subtitle="Operating costs for route optimization" defaultExpanded={false}>
            <Grid container spacing={3}>
              <Grid component="div" size={{ xs: 12, md: 4 }}>
                <Controller
                  name="cost_fixed"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value}
                      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                      label="Fixed Cost"
                      type="number"
                      fullWidth
                      error={!!errors.cost_fixed}
                      helperText={errors.cost_fixed?.message || 'Cost of using this vehicle'}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  )}
                />
              </Grid>              <Grid component="div" size={{ xs: 12, md: 4 }}>
                <Controller
                  name="cost_per_hour"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value}
                      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                      label="Cost per Hour"
                      type="number"
                      fullWidth
                      error={!!errors.cost_per_hour}
                      helperText={errors.cost_per_hour?.message || 'Hourly operating cost'}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  )}
                />
              </Grid>
              <Grid component="div" size={{ xs: 12, md: 4 }}>
                <Controller
                  name="cost_per_km"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value}
                      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                      label="Cost per Kilometer"
                      type="number"
                      fullWidth
                      error={!!errors.cost_per_km}
                      helperText={errors.cost_per_km?.message || 'Distance-based cost'}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Warning for missing locations */}
          {!typedLocations.length && !locationsLoading && (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>No locations available.</strong> Please create locations first before adding vehicles.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={isCurrentlySubmitting || !typedLocations.length || selectedStartLocationId === 0}
            sx={{ minWidth: 120 }}
          >
            {/* ✅ FIXED: Show appropriate loading state for create/update */}
            {isCurrentlySubmitting ? <CircularProgress size={20} /> : (isEditMode ? 'Update Vehicle' : 'Add Vehicle')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};